/**
 * Meta Parameter Builder — Phase 1 + Phase 2
 *
 * Phase 1 (synchronous, top of <head>):
 *   - Writes _fbp (Browser ID) cookie if absent
 *   - Writes _fbc (Click ID) cookie from ?fbclid= URL param
 *
 * Phase 2 (async, non-blocking):
 *   - Reads logged-in Shopify customer data from window._shopifyCustomer
 *   - Normalises + SHA-256 hashes em, ph, fn, ln, ct, st, zp, country
 *   - Persists hashed params in localStorage (_meta_adv)
 *   - On each page load passes stored params to fbq('init') as Advanced Matching
 *     so Meta Pixel carries them in every browser-side event → improves EMQ
 *   - Intercepts newsletter / login / register form submissions to capture
 *     guest customer data and enrich on future visits
 *
 * Spec: https://developers.facebook.com/documentation/ads-commerce/
 *       conversions-api/parameter-builder-library
 *
 * Safety: every section is wrapped in try/catch — no exception from this
 * file can ever break the storefront.
 */
(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════
     PHASE 1 — _fbp / _fbc  (synchronous — runs immediately)
  ═══════════════════════════════════════════════════════════════ */

  function getCookie(name) {
    try {
      var escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + escaped + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }

  function setCookie(name, value, days) {
    try {
      var expires = '';
      if (days) {
        var d = new Date();
        d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
        expires = '; expires=' + d.toUTCString();
      }
      // SameSite=Lax: cookie survives top-level cross-site navigation (Facebook ad click)
      document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
    } catch (e) {}
  }

  function getSubdomainIndex(hostname) {
    return (hostname || '').split('.').length - 1;
  }

  function getUrlParam(paramName) {
    try {
      return new URLSearchParams(window.location.search).get(paramName);
    } catch (e) {
      var m = window.location.search.match(
        new RegExp('[?&]' + paramName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^&]+)')
      );
      return m ? decodeURIComponent(m[1]) : null;
    }
  }

  try {
    var _hostname  = window.location.hostname;
    var _subdomain = getSubdomainIndex(_hostname);
    var _now       = Date.now();

    // _fbp — generate once per browser, skip if already set by Meta Pixel
    if (!getCookie('_fbp')) {
      var _rnd = Math.floor(Math.random() * 2147483647);
      setCookie('_fbp', 'fb.' + _subdomain + '.' + _now + '.' + _rnd, 90);
    }

    // _fbc — refresh on every ad click (fbclid in URL)
    var _fbclid = getUrlParam('fbclid');
    if (_fbclid) {
      setCookie('_fbc', 'fb.' + _subdomain + '.' + _now + '.' + _fbclid, 90);
    }

    window._metaParams = {
      fbp: getCookie('_fbp') || '',
      fbc: getCookie('_fbc') || '',
      customerParams: {}
    };
  } catch (e) {
    window._metaParams = { fbp: '', fbc: '', customerParams: {} };
  }

  /* ═══════════════════════════════════════════════════════════════
     PHASE 2 — Customer parameter hashing + fbq Advanced Matching
  ═══════════════════════════════════════════════════════════════ */

  var PIXEL_ID  = (window._metaPixelId || '').toString().trim();
  var STORE_KEY = '_meta_adv';
  var STORE_VER = 2;

  /* ── Meta normalisation rules ───────────────────────────────── */

  function nEmail(v)    { return (v || '').trim().toLowerCase(); }
  function nPhone(v) {
    // Strip non-digits; prepend Indian country code for 10-digit numbers
    var d = (v || '').replace(/\D/g, '');
    return d.length === 10 ? '91' + d : d;
  }
  function nName(v) {
    return (v || '').trim().toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
  function nCity(v)     { return (v || '').trim().toLowerCase().replace(/\s+/g, ''); }
  function nProvince(v) { return (v || '').trim().toLowerCase().replace(/\s+/g, ''); }
  function nZip(v)      { return (v || '').trim().replace(/\s+/g, ''); }
  function nCountry(v)  { return (v || '').trim().toLowerCase(); }
  // Meta DOB format: YYYYMMDD — strip all non-digits from YYYY-MM-DD date input
  function nDob(v) {
    var s = (v || '').trim().replace(/\D/g, '');
    // Accept 8-digit strings only (YYYYMMDD after stripping)
    return s.length === 8 ? s : '';
  }

  function buildNormalised(data) {
    var n = {};
    if (data.email)     n.em      = nEmail(data.email);
    if (data.phone)     n.ph      = nPhone(data.phone);
    if (data.firstName) n.fn      = nName(data.firstName);
    if (data.lastName)  n.ln      = nName(data.lastName);
    if (data.city)      n.ct      = nCity(data.city);
    if (data.province)  n.st      = nProvince(data.province);
    if (data.zip)       n.zp      = nZip(data.zip);
    if (data.country)   n.country = nCountry(data.country);
    var _dob = nDob(data.dob);
    if (_dob)           n.db      = _dob;
    return n;
  }

  /* ── SHA-256 via SubtleCrypto (async, no external library needed) ── */

  function sha256(str) {
    if (!str || !window.crypto || !window.crypto.subtle) return Promise.resolve('');
    try {
      var buf = new TextEncoder().encode(str);
      return window.crypto.subtle.digest('SHA-256', buf).then(function (ab) {
        return Array.from(new Uint8Array(ab))
          .map(function (b) { return b.toString(16).padStart(2, '0'); })
          .join('');
      }).catch(function () { return ''; });
    } catch (e) { return Promise.resolve(''); }
  }

  /* ── localStorage persistence ───────────────────────────────── */

  function loadStored() {
    try {
      var raw = localStorage.getItem(STORE_KEY);
      if (!raw) return {};
      var obj = JSON.parse(raw);
      return (obj && obj.v === STORE_VER) ? (obj.d || {}) : {};
    } catch (e) { return {}; }
  }

  function mergeStore(incoming) {
    try {
      var cur = loadStored();
      Object.keys(incoming).forEach(function (k) {
        if (incoming[k]) cur[k] = incoming[k]; // never overwrite with empty
      });
      localStorage.setItem(STORE_KEY, JSON.stringify({ v: STORE_VER, d: cur }));
    } catch (e) {}
  }

  /* ── Hash normalised map, merge into store, return hashed object ── */

  function hashAndStore(normalised) {
    var keys = Object.keys(normalised).filter(function (k) { return !!normalised[k]; });
    if (!keys.length) return Promise.resolve({});

    var result = {};
    var tasks  = keys.map(function (k) {
      return sha256(normalised[k]).then(function (h) { if (h) result[k] = h; });
    });
    return Promise.all(tasks).then(function () {
      mergeStore(result);
      return result;
    });
  }

  /* ── fbq Advanced Matching ───────────────────────────────────── */

  function tryFbqInit(params) {
    if (!PIXEL_ID || !Object.keys(params).length) return;
    try { window.fbq('init', PIXEL_ID, params); } catch (e) {}
  }

  // fbq may load after this script (it arrives via content_for_header / deferred)
  function fbqInitWhenReady(params) {
    if (!PIXEL_ID || !Object.keys(params).length) return;
    if (window.fbq) {
      tryFbqInit(params);
    } else {
      var attempts = 0;
      var timer = setInterval(function () {
        if (window.fbq) { clearInterval(timer); tryFbqInit(params); }
        else if (++attempts > 60) { clearInterval(timer); } // give up after 6 s
      }, 100);
    }
  }

  /* ── Form capture — newsletter / login / register ────────────── */

  function captureForm(form) {
    try {
      var data = {};

      var emEl = form.querySelector(
        'input[type="email"], input[name="customer[email]"], input[name="contact[email]"]'
      );
      if (emEl && emEl.value) data.email = emEl.value;

      var fnEl = form.querySelector('input[name="customer[first_name]"]');
      var lnEl = form.querySelector('input[name="customer[last_name]"]');
      if (fnEl && fnEl.value) data.firstName = fnEl.value;
      if (lnEl && lnEl.value) data.lastName  = lnEl.value;

      var phEl = form.querySelector('input[type="tel"], input[name="customer[phone]"]');
      if (phEl && phEl.value) data.phone = phEl.value;

      var dobEl = form.querySelector('input[name="customer[metafields][custom][dob]"]');
      if (dobEl && dobEl.value) data.dob = dobEl.value;

      if (Object.keys(data).length) {
        hashAndStore(buildNormalised(data)).then(function () {
          window._metaParams.customerParams = loadStored();
        });
      }
    } catch (e) {}
  }

  function setupFormCapture() {
    document.addEventListener('submit', function (e) {
      if (e.target && e.target.tagName === 'FORM') captureForm(e.target);
    }, true); // capture phase: runs before the form POST navigates away
  }

  /* ── Phase 2 main init ───────────────────────────────────────── */

  try {
    // 1. Load previously stored hashed params from localStorage immediately
    var _stored = loadStored();
    window._metaParams.customerParams = _stored;

    // 2. Pass stored params to fbq Advanced Matching (uses data from a prior visit)
    fbqInitWhenReady(_stored);

    // 3. If Shopify logged-in customer data was output by theme.liquid Liquid block,
    //    hash it now and store — will enrich fbq on this and future page loads
    if (window._shopifyCustomer) {
      hashAndStore(buildNormalised(window._shopifyCustomer)).then(function (freshHashed) {
        window._metaParams.customerParams = loadStored();
        fbqInitWhenReady(freshHashed);
      });
    }

    // 4. Capture form submissions (newsletter, login, register) for guest users
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setupFormCapture);
    } else {
      setupFormCapture();
    }

  } catch (e) {
    // Phase 2 failure must never crash the storefront
  }

  /* ═══════════════════════════════════════════════════════════════
     PHASE 3 — fb_login_id  +  GoKwik event capture  +  Address enrichment
  ═══════════════════════════════════════════════════════════════ */

  var APP_ID = (window._metaAppId || '').toString().trim();

  /* ── Facebook JS SDK: detect fb_login_id for FB-connected visitors ── */
  // fb_login_id is the Facebook User ID — NOT hashed (passed as plain text).
  // Only fires for users who previously authorised your Facebook App.
  // Requires window._metaAppId (the Facebook App ID, NOT the Pixel ID).
  // The SDK is loaded asynchronously after DOMContentLoaded so it never
  // blocks rendering.
  function loadFbSdk(appId) {
    if (!appId) return; // no-op if App ID not configured in theme settings
    if (document.getElementById('facebook-jssdk')) return; // already loaded
    try {
      window.fbAsyncInit = function () {
        try {
          window.FB.init({ appId: appId, version: 'v21.0', xfbml: false, cookie: false });
          window.FB.getLoginStatus(function (response) {
            if (
              response &&
              response.status === 'connected' &&
              response.authResponse &&
              response.authResponse.userID
            ) {
              var loginId = response.authResponse.userID;
              // Store as plain text — Meta does not require hashing for fb_login_id
              mergeStore({ fb_login_id: loginId });
              window._metaParams.customerParams = loadStored();
              // Re-init fbq with the updated params (includes fb_login_id)
              fbqInitWhenReady(loadStored());
            }
          });
        } catch (e) {}
      };
      var js  = document.createElement('script');
      js.id   = 'facebook-jssdk';
      js.src  = 'https://connect.facebook.net/en_US/sdk.js';
      js.async = true;
      js.defer = true;
      var ref = document.getElementsByTagName('script')[0];
      if (ref && ref.parentNode) ref.parentNode.insertBefore(js, ref);
    } catch (e) {}
  }

  /* ── GoKwik post-checkout event capture ─────────────────────── */
  // GoKwik fires a custom DOM event after a successful order.
  // We listen on all known event names across GoKwik versions.
  // Any customer data in the event detail is hashed and stored.
  function setupGokwikCapture() {
    var GK_EVENTS = [
      'gk-order-success',
      'gokwik-order-success',
      'gk_order_success',
      'gokwikOrderSuccess'
    ];
    GK_EVENTS.forEach(function (evtName) {
      window.addEventListener(evtName, function (e) {
        try {
          var d = (e && e.detail) || {};
          var data = {};

          // Normalise possible key names across GoKwik SDK versions
          var phone    = d.phone    || d.customer_phone || d.customerPhone  || '';
          var email    = d.email    || d.customer_email || d.customerEmail  || '';
          var fn       = d.first_name || d.firstName   || d.customer_name  || '';
          var ln       = d.last_name  || d.lastName    || '';
          var city     = d.city    || d.shipping_city  || d.shippingCity   || '';
          var province = d.province || d.shipping_state || d.state         || '';
          var zip      = d.zip     || d.pincode        || d.postal_code    || '';
          var country  = d.country || d.shipping_country                   || '';

          if (phone)    data.phone    = phone;
          if (email)    data.email    = email;
          if (fn)       data.firstName = fn;
          if (ln)       data.lastName  = ln;
          if (city)     data.city      = city;
          if (province) data.province  = province;
          if (zip)      data.zip       = zip;
          if (country)  data.country   = country;

          if (Object.keys(data).length) {
            hashAndStore(buildNormalised(data)).then(function () {
              window._metaParams.customerParams = loadStored();
            });
          }
        } catch (e) {}
      });
    });
  }

  /* ── Address form enrichment ─────────────────────────────────── */
  // The /account/addresses page lets customers save zip, city, province,
  // country and phone. Capture these on form submit and persist hashed values.
  // This is additive — the Phase 2 submit listener is already attached;
  // this reads the address-specific fields Phase 2 skips.
  function captureAddressFields(form) {
    try {
      var fieldMap = {
        'input[name="address[city]"]':                    'city',
        'input[name="address[zip]"]':                     'zip',
        'input[name="address[phone]"], input[type="tel"]':'phone',
        'input[name="address[first_name]"]':              'firstName',
        'input[name="address[last_name]"]':               'lastName',
        'select[name="address[province]"]':               'province',
        'select[name="address[country]"]':                'country'
      };
      var data = {};
      Object.keys(fieldMap).forEach(function (selector) {
        var el = form.querySelector(selector);
        if (el && el.value) data[fieldMap[selector]] = el.value;
      });
      if (Object.keys(data).length) {
        hashAndStore(buildNormalised(data)).then(function () {
          window._metaParams.customerParams = loadStored();
        });
      }
    } catch (e) {}
  }

  /* ── Phase 3 main init ───────────────────────────────────────── */

  try {
    // 1. Load Facebook JS SDK for fb_login_id (no-op if App ID not in settings)
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { loadFbSdk(APP_ID); });
    } else {
      loadFbSdk(APP_ID);
    }

    // 2. GoKwik checkout success → capture phone / address
    setupGokwikCapture();

    // 3. Address form submissions → capture zip / city / province / country / phone
    document.addEventListener('submit', function (e) {
      if (e.target && e.target.tagName === 'FORM') captureAddressFields(e.target);
    }, true); // capture phase: fires before form POST navigates away

  } catch (e) {
    // Phase 3 failure must never crash the storefront
  }

  /* ═══════════════════════════════════════════════════════════════
     PHASE 4 — GoKwik KwikPass login capture
     KwikPass handles ALL logins for Indian Shopify stores via OTP.
     It does NOT create a Shopify customer session, so the Liquid
     {%- if customer -%} block never fires for these users.
     We capture customer phone/email from two sources:
       a) kp_user_id (localStorage) — persistent device identifier,
          used as external_id for cross-session deduplication
       b) window postMessage — KwikPass iframe sends customer data
          to the parent window after successful OTP login
  ═══════════════════════════════════════════════════════════════ */

  try {
    /* ── a) kp_user_id → external_id (runs every page load) ──── */
    // GoKwik assigns a stable UUID per browser stored in localStorage.
    // Hash it and store as external_id so Meta can deduplicate events
    // across sessions even before the user logs in.
    (function () {
      try {
        var kpUserId = localStorage.getItem('kp_user_id');
        if (kpUserId && kpUserId.trim()) {
          sha256(kpUserId.trim()).then(function (h) {
            if (h) {
              mergeStore({ external_id: h });
              window._metaParams.customerParams = loadStored();
              fbqInitWhenReady(loadStored());
            }
          });
        }
      } catch (e) {}
    })();

    /* ── b) KwikPass postMessage listener ────────────────────── */
    // KwikPass runs inside an iframe at pdp.gokwik.co.
    // After OTP login success it sends a postMessage to the parent window.
    // We extract phone/email from all known GoKwik message shapes.
    window.addEventListener('message', function (evt) {
      try {
        // Security: only process messages from GoKwik / KwikPass origins
        if (!evt.origin || !/gokwik\.co|kwikpass\.co/i.test(evt.origin)) return;

        var payload = evt.data;
        if (!payload) return;
        if (typeof payload === 'string') {
          try { payload = JSON.parse(payload); } catch (e2) { return; }
        }
        if (typeof payload !== 'object') return;

        // Flatten nested shapes: { data: {...} }, { customer: {...} }, { user: {...} }
        var d = payload.data || payload.customer || payload.user || payload;

        // Only act on login / user-identified messages
        var msgType = (payload.type || payload.event || payload.action || '').toString().toLowerCase();
        var isLoginMsg = /login|kwikpass|kp_user|user_data|customer/i.test(msgType);
        var hasPhone   = !!(d.phone || d.mobile || d.mobileNumber || d.phoneNumber || d.customer_phone);
        var hasEmail   = !!(d.email || d.customer_email);

        if (!isLoginMsg && !hasPhone && !hasEmail) return;

        // Build data object from whichever keys are present
        var data = {};
        var phone = d.phone || d.mobile || d.mobileNumber || d.phoneNumber || d.customer_phone || '';
        var email = d.email || d.customer_email || '';
        var name  = d.name  || d.customer_name  || '';
        var fn    = d.first_name  || d.firstName  || (name ? name.split(' ')[0] : '') || '';
        var ln    = d.last_name   || d.lastName   || (name ? name.split(' ').slice(1).join(' ') : '') || '';

        if (phone) data.phone     = phone;
        if (email) data.email     = email;
        if (fn)    data.firstName = fn;
        if (ln)    data.lastName  = ln;

        if (Object.keys(data).length) {
          hashAndStore(buildNormalised(data)).then(function () {
            window._metaParams.customerParams = loadStored();
            fbqInitWhenReady(loadStored());
          });
        }
      } catch (e) {}
    });

    /* ── c) KwikPass custom DOM event listeners ──────────────── */
    // Some GoKwik SDK versions fire CustomEvents on window instead of postMessage.
    var KP_LOGIN_EVENTS = [
      'kwikpass_login', 'kwikpass_login_success', 'kp_login_success',
      'gk-kwikpass-login', 'kwikpassLogin', 'kp-login', 'gk_login_success'
    ];
    KP_LOGIN_EVENTS.forEach(function (evtName) {
      window.addEventListener(evtName, function (e) {
        try {
          var d = (e && e.detail) || {};
          var data = {};
          var phone = d.phone || d.mobile || d.mobileNumber || d.customer_phone || '';
          var email = d.email || d.customer_email || '';
          var fn    = d.first_name || d.firstName || '';
          var ln    = d.last_name  || d.lastName  || '';
          if (phone) data.phone     = phone;
          if (email) data.email     = email;
          if (fn)    data.firstName = fn;
          if (ln)    data.lastName  = ln;
          if (Object.keys(data).length) {
            hashAndStore(buildNormalised(data)).then(function () {
              window._metaParams.customerParams = loadStored();
              fbqInitWhenReady(loadStored());
            });
          }
        } catch (e2) {}
      });
    });

  } catch (e) {
    // Phase 4 failure must never crash the storefront
  }

})();
